import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";

type ToastKind = "info" | "success" | "error";
type Toast = { id: number; message: string; kind: ToastKind; visible: boolean };
type ToastContextValue = { showToast: (message: string, kind?: ToastKind) => void };

const ToastContext = createContext<ToastContextValue | null>(null);
let nextToastId = 1;

export function ToastProvider({ children }: PropsWithChildren) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((message: string, kind: ToastKind = "info") => {
    const id = nextToastId++;
    setToasts((items) => [...items, { id, message, kind, visible: false }]);
    requestAnimationFrame(() => {
      setToasts((items) => items.map((item) => (item.id === id ? { ...item, visible: true } : item)));
    });
    window.setTimeout(() => {
      setToasts((items) => items.map((item) => (item.id === id ? { ...item, visible: false } : item)));
      window.setTimeout(() => {
        setToasts((items) => items.filter((item) => item.id !== id));
      }, 600);
    }, 4000);
  }, []);

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div id="toast-host">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`toast toast-${toast.kind} ${toast.visible ? "toast-visible" : ""}`}
          >
            {toast.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside ToastProvider");
  return ctx;
}
